import { useState, useMemo, useEffect } from 'react'
import { Layout, Menu, Avatar, Typography, Tag, Button, Modal, Form, Input, message, Drawer, Badge } from 'antd'
import {
  DashboardOutlined,
  TeamOutlined,
  AppstoreOutlined,
  ToolOutlined,
  CarOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  FileProtectOutlined,
  ScheduleOutlined,
  DollarOutlined,
  BarChartOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  UserOutlined,
  LockOutlined,
  ShopOutlined,
  BookOutlined,
  RobotOutlined,
  InboxOutlined,
  AimOutlined,
  FundOutlined,
  MessageOutlined,
  CalculatorOutlined,
  SendOutlined,
  MailOutlined,
} from '@ant-design/icons'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import ErrorBoundary from '@/components/ErrorBoundary'
import { useMutation, useQuery } from '@tanstack/react-query'
import { authApi } from '@/api/auth'
import { ticketApi } from '@/api/ticket'
import { notificheApi } from '@/api/notifiche'
import * as intakeCodaApi from '@/api/intakeCoda'
import { interventiApi } from '@/api/interventi'
import client from '@/api/client'
import { useAuthStore } from '@/store/authStore'
import { useIsMobile } from '@/hooks/useBreakpoint'
import SistemaStats from '@/components/Layout/SistemaStats'

const { Sider, Header, Content } = Layout
const { Text } = Typography

const RUOLO_COLOR: Record<string, string> = {
  admin: 'red',
  coordinatore: 'blue',
  tecnico: 'green',
  terzista: 'orange',
  commerciale: 'gold',
}

const menuItems: { key: string; icon: React.ReactNode; label: string; adminOnly?: boolean; children?: { key: string; label: string }[] }[] = [
  { key: '/',               icon: <DashboardOutlined />,   label: 'Dashboard' },
  { key: '/ai-chat',        icon: <MessageOutlined />,    label: 'Agente EDR' },
  { key: '/preventivatore', icon: <CalculatorOutlined />, label: 'Preventivatore' },
  { key: '/pianificazione', icon: <ScheduleOutlined />,   label: 'Pianificazione' },
  {
    key: 'prospect-group',
    icon: <AimOutlined />,
    label: 'Prospect',
    children: [
      { key: '/prospect', label: 'Pipeline' },
      { key: '/contenuti-funnel', label: 'Contenuti Funnel' },
    ],
  },
  {
    key: 'clienti-group',
    icon: <TeamOutlined />,
    label: 'Clienti',
    children: [
      { key: '/clienti', label: 'Elenco' },
      { key: '/punti-vendita', label: 'Punti vendita' },
      { key: '/contratti', label: 'Contratti' },
      { key: '/analisi-rifiuti', label: 'Analisi rifiuto' },
      { key: '/impianti', label: 'Impianti' },
    ],
  },
  { key: '/terzisti',       icon: <CarOutlined />,         label: 'Terzisti' },
  {
    key: 'ticket-group',
    icon: <FileTextOutlined />,
    label: 'Ticket',
    children: [
      { key: '/intake-ai', label: 'Ticket da Rivedere' },
      { key: '/ticket',    label: 'Ticket' },
    ],
  },
  {
    key: 'posta-group',
    icon: <MailOutlined />,
    label: 'Posta',
    children: [
      { key: '/posta-in-arrivo', label: 'Posta in arrivo' },
      { key: '/posta-in-uscita', label: 'Posta in uscita' },
    ],
  },
  {
    key: 'interventi-group',
    icon: <ToolOutlined />,
    label: 'Interventi',
    children: [
      { key: '/interventi', label: 'Interventi' },
      { key: '/post-intervento', label: 'Post Intervento' },
    ],
  },
  { key: '/fatturazione',   icon: <DollarOutlined />,      label: 'Fatturazione' },
  { key: '/gps',            icon: <EnvironmentOutlined />, label: 'GPS Live' },
  { key: '/analytics',      icon: <BarChartOutlined />,    label: 'Analytics' },
  { key: '/costi',          icon: <FundOutlined />,        label: 'Costi', adminOnly: true },
  { key: '/archivio',       icon: <InboxOutlined />,       label: 'Archivio' },
  { key: '/manuale',        icon: <BookOutlined />,        label: 'Manuale' },
  { key: '/configurazione', icon: <SettingOutlined />,     label: 'Configurazione' },
]

export default function AppLayout() {
  // Responsive: su mobile (≤ 768px) la sidebar è un Drawer chiudibile.
  // Su desktop resta fissa e collassabile a 80px.
  const isMobile = useIsMobile()
  const [collapsed, setCollapsed] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [pwdModalOpen, setPwdModalOpen] = useState(false)
  const [pwdForm] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { utente, clearAuth } = useAuthStore()

  const { data: intakePendentiCount } = useQuery({
    queryKey: ['intake-pendenti-count'],
    queryFn: () => ticketApi.countPendenti(),
    refetchInterval: 30_000,
    select: (d: { count: number }) => d.count,
    enabled: !utente?.ruoli?.includes('terzista'),
    retry: false,
  })

  const { data: approvazionePendentiCount } = useQuery({
    queryKey: ['notifiche-count-da-approvare'],
    queryFn: () => notificheApi.countDaApprovare(),
    refetchInterval: 30_000,
    select: (d: { count: number }) => d.count,
    enabled: !utente?.ruoli?.includes('terzista'),
    retry: false,
  })

  const { data: intakeCodaPendentiCount } = useQuery({
    queryKey: ['intake-coda-count-pending'],
    queryFn: () => intakeCodaApi.countPending(),
    refetchInterval: 30_000,
    enabled: !utente?.ruoli?.includes('terzista'),
    retry: false,
  })

  // Richieste di variazione data mandate dai terzisti dall'app (18.25)
  const { data: riprogrammazioniCount } = useQuery({
    queryKey: ['interventi-count-riprogrammazioni'],
    queryFn: () => interventiApi.countRiprogrammazioni(),
    refetchInterval: 30_000,
    select: (d: { count: number }) => d.count,
    enabled: !utente?.ruoli?.includes('terzista'),
    retry: false,
  })

  // Alert attivi (18.46): generici + quelli assegnati specificamente a me
  const { data: alertCount } = useQuery({
    queryKey: ['alert-count'],
    queryFn: async () => (await client.get('/alert/count')).data as { count: number },
    refetchInterval: 30_000,
    select: (d: { count: number }) => d.count,
    enabled: !utente?.ruoli?.includes('terzista'),
    retry: false,
  })

  // Utente "solo-commerciale": ha il ruolo commerciale ma NON admin/coordinatore.
  // Può aprire esclusivamente Preventivatore e Agente EDR; la sua home è il
  // Preventivatore. Chi è anche admin/coordinatore non è limitato.
  const ruoli = utente?.ruoli ?? []
  const soloCommerciale =
    ruoli.includes('commerciale') && !ruoli.includes('admin') && !ruoli.includes('coordinatore')
  const ROTTE_COMMERCIALE = ['/preventivatore', '/ai-chat']
  const pathConsentitoCommerciale = ROTTE_COMMERCIALE.some(
    (r) => location.pathname === r || location.pathname.startsWith(r + '/'),
  )

  useEffect(() => {
    if (soloCommerciale && !pathConsentitoCommerciale) {
      navigate('/preventivatore', { replace: true })
      message.info('Accesso consentito solo a Preventivatore e Agente EDR')
    }
  }, [location.pathname, soloCommerciale, pathConsentitoCommerciale, navigate])

  // Heartbeat presenza: mentre l'utente è autenticato segnala la propria
  // presenza ogni 15s (alimenta il tab "Utenti collegati"). Fail-silent.
  useEffect(() => {
    if (!utente) return
    const ping = () => { client.post('/utenti/presenza/heartbeat').catch(() => {}) }
    ping()
    const t = setInterval(ping, 15_000)
    return () => clearInterval(t)
  }, [utente])

  const handleLogout = async () => {
    await authApi.logout()
    clearAuth()
    navigate('/login', { replace: true })
  }

  const cambiaPasswordMutation = useMutation({
    mutationFn: (values: { password_attuale: string; nuova_password: string }) =>
      client.patch('/utenti/me/password', values),
    onSuccess: () => {
      message.success('Password aggiornata')
      setPwdModalOpen(false)
      pwdForm.resetFields()
    },
    onError: (e: { response?: { data?: { detail?: string } } }) => {
      message.error(e?.response?.data?.detail ?? 'Errore nel cambio password')
    },
  })

  // Determina la voce attiva dal path corrente
  const selectedKey = menuItems
    .slice()
    .reverse()
    .find((item) => {
      if (location.pathname.startsWith(item.key) || location.pathname === item.key) return true
      if (item.children) return item.children.some(c => location.pathname.startsWith(c.key))
      return false
    })
    ?.key ?? '/'

  const menuItemsWithBadges = useMemo(
    () => {
      return menuItems
        .filter((item) => !item.adminOnly || utente?.ruoli?.includes('admin'))
        .map((item) => {
          // Handle submenu items with badges on children
          if (item.children) {
            return {
              ...item,
              children: item.children.map(child => {
                if (child.key === '/posta-in-arrivo' && intakeCodaPendentiCount && intakeCodaPendentiCount > 0) {
                  return {
                    key: child.key,
                    label: (
                      <span>
                        Posta in arrivo
                        <Badge count={intakeCodaPendentiCount} size="small" offset={[6, -2]} style={{ backgroundColor: '#faad14' }} />
                      </span>
                    ),
                  }
                }
                if (child.key === '/posta-in-uscita' && approvazionePendentiCount && approvazionePendentiCount > 0) {
                  return {
                    key: child.key,
                    label: (
                      <span>
                        Posta in uscita
                        <Badge count={approvazionePendentiCount} size="small" offset={[6, -2]} style={{ backgroundColor: '#ff4d4f' }} />
                      </span>
                    ),
                  }
                }
                if (child.key === '/interventi' && riprogrammazioniCount && riprogrammazioniCount > 0) {
                  return {
                    key: child.key,
                    label: (
                      <span>
                        Interventi
                        <Badge count={riprogrammazioniCount} size="small" offset={[6, -2]} style={{ backgroundColor: '#faad14' }} />
                      </span>
                    ),
                  }
                }
                if (child.key === '/intake-ai' && intakePendentiCount && intakePendentiCount > 0) {
                  return {
                    key: child.key,
                    label: (
                      <span>
                        Ticket da Rivedere
                        <Badge count={intakePendentiCount} size="small" offset={[6, -2]} style={{ backgroundColor: '#ff4d4f' }} />
                      </span>
                    ),
                  }
                }
                return { key: child.key, label: child.label }
              }),
            }
          }
          if (item.key === '/' && alertCount && alertCount > 0) {
            return {
              ...item,
              label: (
                <span>
                  Dashboard
                  <Badge count={alertCount} size="small" offset={[6, -2]} style={{ backgroundColor: '#faad14' }} />
                </span>
              ),
            }
          }
          return item
        })
    },
    [intakePendentiCount, approvazionePendentiCount, intakeCodaPendentiCount, riprogrammazioniCount, alertCount, utente],
  )

  // Contenuto della sidebar (riusato sia in Sider desktop che in Drawer mobile)
  const sidebarContent = (
    <>
      <div style={{
        padding: collapsed && !isMobile ? '16px 8px' : '16px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        marginBottom: 8,
        textAlign: 'center',
        transition: 'all 0.2s',
      }}>
        <img
          src="/logo.png"
          alt="EDR"
          style={{
            maxHeight: 40,
            maxWidth: collapsed && !isMobile ? 40 : 140,
            objectFit: 'contain',
            transition: 'all 0.2s',
          }}
        />
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKey]}
        style={{ background: 'transparent', border: 'none' }}
        onClick={({ key }) => {
          if (soloCommerciale && !ROTTE_COMMERCIALE.includes(key)) {
            message.warning('Accesso consentito solo a Preventivatore e Agente EDR')
            return
          }
          navigate(key)
          if (isMobile) setDrawerOpen(false)
        }}
        items={menuItemsWithBadges}
      />
    </>
  )

  return (
    <Layout style={{ height: 'var(--edr-vh)', overflow: 'hidden' }}>
      {/* Sidebar desktop — solo su schermi ≥ 768px */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={250}
          style={{
            background: 'linear-gradient(180deg, #0a1628 0%, #1a3a6b 100%)',
            position: 'fixed',
            height: '100vh',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
            overflow: 'auto',
          }}
        >
          {sidebarContent}
        </Sider>
      )}

      {/* Sidebar mobile — Drawer laterale sinistro */}
      {isMobile && (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={290}
          styles={{
            body: {
              padding: 0,
              background: 'linear-gradient(180deg, #0a1628 0%, #1a3a6b 100%)',
            },
            header: { display: 'none' },
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      <Layout style={{
        marginLeft: isMobile ? 0 : (collapsed ? 80 : 250),
        transition: 'all 0.2s',
        height: 'var(--edr-vh)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Header style={{
          background: '#fff',
          padding: isMobile ? '0 12px' : '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          position: 'sticky',
          top: 0,
          zIndex: 99,
          height: 56,
        }}>
          <Button
            type="text"
            icon={
              isMobile
                ? <MenuOutlined />
                : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)
            }
            onClick={() => {
              if (isMobile) setDrawerOpen(true)
              else setCollapsed(!collapsed)
            }}
            style={{ fontSize: 20 }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 12 }}>
            {!isMobile && <SistemaStats />}
            <Avatar size={isMobile ? 'small' : 'default'} style={{ background: '#1a3a6b' }} icon={<UserOutlined />} />
            {utente && !isMobile && (
              <div style={{ lineHeight: 1.3 }}>
                <Text strong style={{ display: 'block', fontSize: 13 }}>
                  {utente.nome} {utente.cognome}
                </Text>
                {utente.ruoli.map(r => (
                  <Tag key={r} color={RUOLO_COLOR[r]} style={{ margin: 0, fontSize: 11 }}>
                    {r}
                  </Tag>
                ))}
              </div>
            )}
            <Button
              type="text"
              icon={<LockOutlined />}
              onClick={() => { pwdForm.resetFields(); setPwdModalOpen(true) }}
              title="Cambia password"
            />
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              danger
              title="Esci"
            />
          </div>
        </Header>

        <Content style={{
          margin: isMobile ? 12 : 24,
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        }}>
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </Content>
      </Layout>

      <Modal
        title="Cambia password"
        open={pwdModalOpen}
        onCancel={() => setPwdModalOpen(false)}
        onOk={() => pwdForm.submit()}
        confirmLoading={cambiaPasswordMutation.isPending}
        okText="Salva"
        cancelText="Annulla"
        width={isMobile ? '95%' : 520}
      >
        <Form
          form={pwdForm}
          layout="vertical"
          style={{ marginTop: 16 }}
          onFinish={(values) => cambiaPasswordMutation.mutate(values)}
        >
          <Form.Item name="password_attuale" label="Password attuale" rules={[{ required: true, message: 'Inserisci la password attuale' }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="nuova_password"
            label="Nuova password"
            rules={[{
              validator: (_, value) => {
                if (!value) return Promise.reject('Inserisci la nuova password')
                if (value.length < 8) return Promise.reject('Minimo 8 caratteri')
                if (!/[a-zA-Z]/.test(value) || !/[0-9]/.test(value))
                  return Promise.reject('Deve contenere almeno una lettera e un numero')
                return Promise.resolve()
              },
            }]}
            extra="Minimo 8 caratteri, almeno una lettera e un numero."
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="conferma_password"
            label="Conferma nuova password"
            dependencies={['nuova_password']}
            rules={[
              { required: true, message: 'Conferma la nuova password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('nuova_password') === value) return Promise.resolve()
                  return Promise.reject('Le password non coincidono')
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  )
}
